import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {Box, List, ListItemButton, Typography} from '@mui/material';
import {type ReactNode, useMemo, useState} from 'react';

export type PathTreeItem = {path: string; meta?: ReactNode};
/** フォルダは children を持つ。ファイルの path は元の item.path */
export type PathTreeNode = {
  name: string;
  path: string;
  meta?: ReactNode;
  children?: PathTreeNode[];
};

const byName = (a: PathTreeNode, b: PathTreeNode) => a.name.localeCompare(b.name);
const sortNodes = (nodes: PathTreeNode[]): PathTreeNode[] => [
  ...nodes
    .filter(node => node.children)
    .sort(byName)
    .map(node => ({...node, children: sortNodes(node.children!)})),
  ...nodes.filter(node => !node.children).sort(byName)
];

/** `md:/sub/x.ls` のようなパス一覧を `/` 区切りのフォルダ/ファイルのツリーにする */
export const buildPathTree = (items: PathTreeItem[]): PathTreeNode[] => {
  const root: PathTreeNode[] = [];
  for (const item of items) {
    const segments = item.path.split('/').filter(Boolean);
    let level = root;
    segments.slice(0, -1).forEach((name, index) => {
      let folder = level.find(node => node.children && node.name === name);
      if (!folder) {
        folder = {name, path: segments.slice(0, index + 1).join('/'), children: []};
        level.push(folder);
      }
      level = folder.children!;
    });
    level.push({
      name: segments[segments.length - 1] ?? item.path,
      path: item.path,
      meta: item.meta
    });
  }
  return sortNodes(root);
};

type Props = {
  items: PathTreeItem[];
  selectedPath: string | null;
  onSelect: (path: string) => void;
  /** 一覧のアクセシブルネーム */
  label: string;
};

/** パス文字列だけから組み立てる読み取り専用のファイルツリー（FileTree と同じ見た目） */
export default function PathTree({items, selectedPath, onSelect, label}: Props) {
  const tree = useMemo(() => buildPathTree(items), [items]);
  // フォルダは少ないので既定で展開し、閉じたものだけ覚える
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (path: string) =>
    setCollapsed(current => {
      const next = new Set(current);
      if (!next.delete(path)) next.add(path);
      return next;
    });

  const renderNodes = (nodes: PathTreeNode[], depth: number): ReactNode[] =>
    nodes.flatMap(node => {
      const isFolder = !!node.children;
      const expanded = isFolder && !collapsed.has(node.path);
      const selected = !isFolder && node.path === selectedPath;
      const row = (
        <ListItemButton
          key={(isFolder ? 'd:' : 'f:') + node.path}
          onClick={() => (isFolder ? toggle(node.path) : onSelect(node.path))}
          selected={selected}
          aria-expanded={isFolder ? expanded : undefined}
          aria-current={selected ? 'true' : undefined}
          title={isFolder ? undefined : node.path}
          sx={{pl: 1 + depth * 2, py: 0.25, minHeight: 32}}
        >
          {!isFolder ? (
            <>
              <Box sx={{width: 24, mr: 0.5}} />
              <InsertDriveFileIcon fontSize="small" sx={{color: 'action.active', mr: 1}} />
            </>
          ) : (
            <>
              {expanded ? (
                <ExpandMoreIcon fontSize="small" sx={{mr: 0.5}} />
              ) : (
                <ChevronRightIcon fontSize="small" sx={{mr: 0.5}} />
              )}
              <FolderIcon fontSize="small" sx={{color: 'warning.main', mr: 1}} />
            </>
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: isFolder ? 'medium' : 'normal',
              // 名前を優先して表示し、幅が足りないときは件数・日時の側を省略する
              flex: '1 0 auto',
              maxWidth: '70%',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {node.name}
          </Typography>
          {node.meta && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ml: 1, flex: '0 1 auto', minWidth: 0, textAlign: 'right'}}
            >
              {node.meta}
            </Typography>
          )}
        </ListItemButton>
      );
      return expanded ? [row, ...renderNodes(node.children!, depth + 1)] : [row];
    });

  return (
    <List
      dense
      disablePadding
      aria-label={label}
      sx={{flex: 1, minHeight: 0, overflow: 'auto', '& .MuiListItemButton-root': {borderRadius: 0}}}
    >
      {renderNodes(tree, 0)}
    </List>
  );
}
